import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  Row,
  Col,
  Spin,
  Typography,
  Table,
  Tag,
  Empty,
  Input,
  DatePicker,
  Space,
  Modal,
  Descriptions,
  Divider,
} from "antd";
import {
  WalletOutlined,
  PlusCircleOutlined,
  HistoryOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserWallet,
  getWalletHistory,
} from "../../redux/Actions/walletAction";
import { getWalletReducer } from "../../redux/Reducer/walletReducer";
import FundWalletModal from "./FundWalletModal";
import moment from "moment";

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;

const WalletDashboard = ({ authuserdetails }) => {
  const dispatch = useDispatch();
  const {
    walletData,
    isLoadingWallet,
    walletHistory,
    walletPagination,
    isLoadingWalletHistory,
  } = useSelector(getWalletReducer);

  const [isFundModalOpen, setIsFundModalOpen] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [isTxModalOpen, setIsTxModalOpen] = useState(false);

  const wallet = walletData?.wallet || {};

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState([]);

  const fetchHistory = (page = 1, search = "", dates = []) => {
    let payload = { page };

    if (search) {
      payload.search = search;
    }

    if (dates && dates.length === 2) {
      payload.start_date = dates[0].format("YYYY-MM-DD");
      payload.end_date = dates[1].format("YYYY-MM-DD");
    }

    dispatch(getWalletHistory(payload));
  };

  useEffect(() => {
    if (authuserdetails?.id) {
      dispatch(getUserWallet(authuserdetails.id));
      fetchHistory(1, searchQuery, dateRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, authuserdetails]);

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    fetchHistory(pagination.current, searchQuery, dateRange);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
    fetchHistory(1, value, dateRange);
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    setCurrentPage(1);
    fetchHistory(1, searchQuery, dates);
  };

  const handleFundWallet = () => {
    setIsFundModalOpen(true);
  };

  const showTxDetails = (record) => {
    setSelectedTx(record);
    setIsTxModalOpen(true);
  };

  const FormatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(amount || 0);
  };

  // --- HELPER FUNCTIONS FOR TRANSACTION FORMATTING ---
  const getTransactionTypeLabel = (type, source) => {
    const safeType = String(type).toLowerCase();
    const safeSource = String(source).toLowerCase();

    if (safeType === "deposit") {
      if (safeSource === "reward") return "Cashback Reward";
      if (safeSource === "refund") return "Refund Received";
      if (safeSource === "gateway_payment") return "Wallet Funded";
      return "Deposit";
    }
    if (safeType === "debit") {
      if (safeSource === "order_payment") return "Order Payment";
      return "Debit";
    }
    return safeType;
  };

  const getChannelLabel = (source, gateway) => {
    const safeSource = String(source).toLowerCase();
    if (safeSource === "gateway_payment")
      return gateway ? String(gateway).toUpperCase() : "Card/Transfer";
    if (safeSource === "reward" || safeSource === "refund") return "Internal";
    if (safeSource === "order_payment") return "Checkout";
    return "System";
  };

  // Setup Simplified Columns
  const columns = [
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => (
        <span style={{ fontSize: "13px", color: "#555", whiteSpace: "nowrap" }}>
          {text ? moment(text).format("MMM DD, YYYY") : "N/A"}
        </span>
      ),
    },
    {
      title: "Channel",
      render: (_, record) => (
        <span
          style={{
            fontSize: "12px",
            color: "#888",
            textTransform: "capitalize",
          }}
        >
          {getChannelLabel(record.source, record.payment_gateway)}
        </span>
      ),
    },
    {
      title: "Type",
      render: (_, record) => {
        const isCredit = String(record.type).toLowerCase() === "deposit";
        const label = getTransactionTypeLabel(record.type, record.source);
        return (
          <span
            style={{
              color: isCredit ? "#52c41a" : "#ff4d4f",
              fontWeight: 500,
              textTransform: "capitalize",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            {isCredit ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
            {label}
          </span>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (text, record) => {
        const isCredit = String(record.type).toLowerCase() === "deposit";
        return (
          <span
            style={{
              color: isCredit ? "#52c41a" : "#ff4d4f",
              fontWeight: "bold",
            }}
          >
            {isCredit ? "+" : "-"}
            {FormatCurrency(text)}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => {
        const safeText = String(text).trim().toLowerCase();
        let color =
          safeText === "completed"
            ? "green"
            : safeText === "pending"
              ? "orange"
              : "red";
        return (
          <Tag color={color} style={{ textTransform: "capitalize", margin: 0 }}>
            {safeText}
          </Tag>
        );
      },
    },
    {
      title: "",
      key: "action",
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => showTxDetails(record)}
          size="small"
          style={{ color: "#252A4D", fontWeight: 500 }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div className="container mt-4">
      {/* 
        👉 FIX: Reverted to side-by-side layout for desktop. 
        It only stacks on very small screens (mobile).
      */}
      <Row gutter={[24, 24]}>
        {/* Wallet Balance Card - Reverted Width */}
        <Col xs={24} md={10} lg={9}>
          <Card
            className="wallet-card"
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #252A4D 0%, #1a1d3a 100%)",
              borderRadius: "12px",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(37, 42, 77, 0.2)",
            }}
          >
            <Spin spinning={isLoadingWallet} tip="Loading Wallet...">
              <div style={{ padding: "10px" }}>
                <div
                  className="d-flex justify-content-between align-items-start mb-4"
                  style={{ flexWrap: "wrap", gap: "10px" }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      opacity: 0.8,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <WalletOutlined className="mr-2" /> My Wallet Balance
                  </span>
                  {wallet.status === "active" ? (
                    <div
                      style={{
                        background: "rgba(82, 196, 26, 0.2)",
                        color: "#52c41a",
                        padding: "2px 10px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      Active
                    </div>
                  ) : (
                    <div
                      style={{
                        background: "rgba(255, 77, 79, 0.2)",
                        color: "#ff4d4f",
                        padding: "2px 10px",
                        borderRadius: "20px",
                        fontSize: "12px",
                      }}
                    >
                      Inactive
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <h1
                    style={{
                      color: "#fff",
                      margin: 0,
                      fontSize: "36px",
                      fontWeight: "bold",
                      wordBreak: "break-word",
                    }}
                  >
                    {FormatCurrency(wallet.balance)}
                  </h1>
                  <Text style={{ color: "rgba(255,255,255,0.6)" }}>
                    Currency: {wallet.currency || "NGN"}
                  </Text>
                </div>

                <Button
                  type="primary"
                  size="large"
                  icon={<PlusCircleOutlined />}
                  onClick={handleFundWallet}
                  style={{
                    backgroundColor: "#BDC167",
                    borderColor: "#BDC167",
                    color: "#252A4D",
                    fontWeight: "bold",
                    width: "100%",
                    height: "45px",
                    borderRadius: "8px",
                    marginTop: "10px",
                  }}
                >
                  Fund Wallet
                </Button>
              </div>
            </Spin>
          </Card>
        </Col>

        {/* Recent Transactions Card - Reverted Width */}
        <Col xs={24} md={14} lg={15}>
          <Card
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "15px",
                  padding: "5px 0",
                }}
              >
                <span
                  style={{
                    color: "#252A4D",
                    fontWeight: 600,
                    fontSize: "16px",
                  }}
                >
                  <HistoryOutlined /> Transaction History
                </span>

                {/* FILTERS */}
                <Space
                  wrap
                  style={{ width: "100%", justifyContent: "flex-end" }}
                >
                  <Search
                    placeholder="Search Ref..."
                    allowClear
                    onSearch={handleSearch}
                    style={{ width: "100%", maxWidth: 180, display: "none" }}
                  />
                  <RangePicker
                    onChange={handleDateChange}
                    style={{ width: "100%", maxWidth: 220 }}
                  />
                </Space>
              </div>
            }
            bordered={false}
            style={{ borderRadius: "12px", minHeight: "100%" }}
            styles={{ body: { padding: 0 } }}
          >
            <Spin spinning={isLoadingWalletHistory}>
              {walletHistory && walletHistory.length > 0 ? (
                <Table
                  dataSource={walletHistory}
                  columns={columns}
                  rowKey="id"
                  onChange={handleTableChange}
                  pagination={{
                    current: currentPage,
                    total: walletPagination?.total || 0,
                    pageSize: walletPagination?.pageSize || 20,
                    showSizeChanger: false,
                  }}
                  size="small"
                  scroll={{ x: "max-content" }}
                />
              ) : (
                <div style={{ padding: "60px 0" }}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <span style={{ color: "#999" }}>
                        No transactions found
                      </span>
                    }
                  />
                </div>
              )}
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* Funding Modal */}
      <FundWalletModal
        open={isFundModalOpen}
        onCancel={() => setIsFundModalOpen(false)}
        walletId={wallet.uuid}
      />

      {/* Transaction Details Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <EyeOutlined /> <span>Transaction Details</span>
          </div>
        }
        open={isTxModalOpen}
        onCancel={() => setIsTxModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsTxModalOpen(false)}>
            Close
          </Button>,
        ]}
        centered
      >
        {selectedTx && (
          <div className="pt-3">
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <Text type="secondary" style={{ textTransform: "capitalize" }}>
                {selectedTx.type} Amount
              </Text>
              <h2
                style={{
                  margin: 0,
                  color:
                    selectedTx.type === "deposit" ||
                    selectedTx.type === "credit"
                      ? "#52c41a"
                      : "#ff4d4f",
                }}
              >
                {FormatCurrency(selectedTx.amount)}
              </h2>
              <Tag
                color={
                  selectedTx.status === "completed"
                    ? "green"
                    : selectedTx.status === "pending"
                      ? "orange"
                      : "red"
                }
                style={{ marginTop: "5px", textTransform: "capitalize" }}
              >
                {selectedTx.status}
              </Tag>
            </div>

            <Divider style={{ margin: "15px 0" }} />

            <Descriptions
              column={1}
              size="small"
              labelStyle={{ color: "#888" }}
            >
              <Descriptions.Item label="Transaction ID">
                <Text copyable style={{ fontWeight: 500 }}>
                  {selectedTx.reference_id}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Date">
                <Text strong>
                  {moment(selectedTx.created_at).format(
                    "MMMM Do YYYY, h:mm:ss a",
                  )}
                </Text>
              </Descriptions.Item>
              {selectedTx.balance_before && (
                <Descriptions.Item label="Balance Before">
                  <Text>{FormatCurrency(selectedTx.balance_before)}</Text>
                </Descriptions.Item>
              )}
              {selectedTx.balance_after && (
                <Descriptions.Item label="Balance After">
                  <Text>{FormatCurrency(selectedTx.balance_after)}</Text>
                </Descriptions.Item>
              )}
              {/* <Descriptions.Item label="Initiated By">
                <Text style={{ textTransform: "capitalize" }}>
                  {selectedTx.initiated_by || "System"}
                </Text>
              </Descriptions.Item> */}
            </Descriptions>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WalletDashboard;
